/**
 * Central Formatting Utilities
 * 
 * Provides consistent formatting for currency, dates, numbers, percentages,
 * file sizes, and payment methods across the application.
 */

// ============================================================================
// Types
// ============================================================================

export interface CurrencyOptions {
  currency?: string;
  locale?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  compact?: boolean;
}

export interface NumberOptions {
  locale?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  compact?: boolean;
}

export type DateFormat = "short" | "long" | "relative" | "iso";

// ============================================================================
// Currency Formatting
// ============================================================================

/**
 * Format a number as currency
 * @param amount - The amount to format
 * @param options - Formatting options
 * @returns Formatted currency string (e.g., "$1,234,567")
 */
export function formatCurrency(
  amount: number | null | undefined,
  options: CurrencyOptions = {}
): string {
  if (amount === null || amount === undefined) return "$0";
  
  const {
    currency = "USD",
    locale = "en-US",
    minimumFractionDigits = 0,
    maximumFractionDigits = 0,
    compact = false,
  } = options;

  if (compact) {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(amount);
  }

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(amount);
}

// ============================================================================
// Date Formatting
// ============================================================================

/**
 * Format a date with various preset formats
 * @param date - Date string or Date object
 * @param format - Format preset: "short", "long", "relative", or "iso"
 * @returns Formatted date string
 */
export function formatDate(
  date: string | Date | null | undefined,
  format: DateFormat = "short"
): string {
  if (!date) return "—";
  
  // A bare `YYYY-MM-DD` is a CALENDAR date with no timezone. `new Date()` parses it as
  // UTC midnight per the spec, and every getter below is LOCAL — so west of UTC the day
  // rolls backwards. MEASURED in America/Chicago: formatDate("2026-06-01") returned
  // "05/31/2026", and a 06/01–09/30 campaign rendered as 05/31/2026 – 09/29/2026.
  //
  // Parsing the parts by hand pins it to local midnight, so the day-of-month that comes
  // out is the one that went in. Timestamps WITH a time or zone (`...T...`, `...Z`) are
  // untouched: those are real instants and converting them to local time is correct.
  const dateObj =
    typeof date === "string"
      ? /^\d{4}-\d{2}-\d{2}$/.test(date.trim())
        ? (() => {
            const [y, m, d] = date.trim().split("-").map(Number);
            return new Date(y, m - 1, d);
          })()
        : new Date(date)
      : date;
  
  if (isNaN(dateObj.getTime())) return "—";

  switch (format) {
    case "short": {
      const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
      const dd = String(dateObj.getDate()).padStart(2, "0");
      const yyyy = dateObj.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    }
    
    case "long":
      return dateObj.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    
    case "relative":
      return formatRelativeDate(dateObj);
    
    case "iso":
      return dateObj.toISOString().split("T")[0];
    
    default: {
      const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
      const dd = String(dateObj.getDate()).padStart(2, "0");
      const yyyy = dateObj.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    }
  }
}

/**
 * Format a date relative to now (e.g., "2 days ago", "in 3 hours")
 */
function formatRelativeDate(date: Date): string {
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.round(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.round(diffMs / (1000 * 60));

  if (Math.abs(diffMinutes) < 60) {
    if (diffMinutes === 0) return "just now";
    return diffMinutes > 0 ? `in ${diffMinutes} min` : `${Math.abs(diffMinutes)} min ago`;
  }
  
  if (Math.abs(diffHours) < 24) {
    return diffHours > 0 ? `in ${diffHours} hours` : `${Math.abs(diffHours)} hours ago`;
  }
  
  if (Math.abs(diffDays) < 30) {
    return diffDays > 0 ? `in ${diffDays} days` : `${Math.abs(diffDays)} days ago`;
  }
  
  // Fall back to short format for dates more than a month away
  return formatDate(date, "short");
}

// ============================================================================
// Percentage Formatting
// ============================================================================

/**
 * TWO FUNCTIONS, NOT ONE WITH A FLAG.
 *
 * These replaced `formatPercentage(value, decimals, isDecimal = true)`. The flag was the problem:
 * `0.5` and `50` are both "fifty percent" to a reader, so nothing about a CALL SITE revealed which
 * convention its value carried — the answer lived in a third positional boolean that defaulted to
 * one of the two answers. Getting it wrong is silent and exactly 100x, in whichever direction.
 *
 * That it defaulted to FRACTION is why `formatPercentage(x / 100)` appeared at fifteen-odd call
 * sites: callers holding a whole percent divided it down so the function could multiply it back
 * up. Two names delete both the flag and the division.
 *
 * WHICH ONE DO I WANT? Look at the column, not the name — the precision decides it and cannot lie:
 *
 *   NUMERIC(5,4) caps at 9.9999   -> cannot hold 25 for 25%   -> FRACTION  -> ...FromFraction
 *   NUMERIC(5,2) caps at 999.99   -> holds 25 for 25%         -> WHOLE     -> ...FromWhole
 *
 * Measured on production 2026-08-21: 19 columns are fraction-only, 34 hold whole percent, 10 are
 * ambiguous at NUMERIC(6,4)/(5,3). There is no app-wide convention to appeal to, which is exactly
 * why the unit must be stated at the call site rather than assumed.
 */

/** 0.0825 -> "8.25%". For fraction-carrying values: NUMERIC(5,4) and friends. */
export function formatPercentFromFraction(
  value: number | null | undefined,
  decimals: number = 0
): string {
  if (value === null || value === undefined) return "0%";
  return `${(value * 100).toFixed(decimals)}%`;
}

/** 8.25 -> "8.25%". For whole-percent values: NUMERIC(5,2) and friends. No division. */
export function formatPercentFromWhole(
  value: number | null | undefined,
  decimals: number = 0
): string {
  if (value === null || value === undefined) return "0%";
  return `${value.toFixed(decimals)}%`;
}

// ============================================================================
// Number Formatting
// ============================================================================

/**
 * Format a number with optional compact notation (1.2M, 45K)
 * @param value - The number to format
 * @param options - Formatting options
 * @returns Formatted number string
 */
export function formatNumber(
  value: number | null | undefined,
  options: NumberOptions = {}
): string {
  if (value === null || value === undefined) return "0";
  
  const {
    locale = "en-US",
    minimumFractionDigits = 0,
    maximumFractionDigits = 2,
    compact = false,
  } = options;

  if (compact) {
    return new Intl.NumberFormat(locale, {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value);
  }

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value);
}

// ============================================================================
// File Size Formatting
// ============================================================================

/**
 * Format bytes into human-readable file size
 * @param bytes - The number of bytes
 * @returns Formatted file size string (e.g., "1.5 MB")
 */
export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || bytes === 0) return "0 Bytes";
  
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, i);
  
  return `${Math.round(size * 100) / 100} ${sizes[i]}`;
}

// ============================================================================
// Payment Method Formatting
// ============================================================================

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  ach: "ACH",
  wire: "Wire Transfer",
  check: "Check",
  card: "Card",
  plastiq: "Plastiq",
  credit_card: "Credit Card",
  virtual_card: "Virtual Card",
  cash: "Cash",
};

/**
 * Format a payment method code into a human-readable label
 * @param method - The payment method code
 * @returns Formatted payment method label
 */
export function formatPaymentMethod(method: string | null | undefined): string {
  if (!method) return "—";
  
  const normalizedMethod = method.toLowerCase().replace(/[_\s-]/g, "_");
  return PAYMENT_METHOD_LABELS[normalizedMethod] || method;
}

// ============================================================================
// Status Formatting
// ============================================================================

/**
 * Format a status string for display (capitalize, replace underscores)
 * @param status - The status code
 * @returns Formatted status label
 */
export function formatStatus(status: string | null | undefined): string {
  if (!status) return "—";
  
  return status
    .replace(/_/g, " ")
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

// ============================================================================
// Rebate & Specialty Formatters (migrated from utils.ts)
// ============================================================================

/**
 * Format a decimal value as a rebate rate percentage (e.g., 0.0125 → "1.25%")
 */
export function formatRebateRate(decimalValue: number): string {
  return `${(decimalValue * 100).toFixed(2)}%`;
}

/**
 * Parse a percentage string back to decimal (e.g., "1.25%" → 0.0125)
 */
export function parsePercentageToDecimal(percentString: string): number {
  const cleanedValue = percentString.replace(/[%,\s]/g, '');
  return parseFloat(cleanedValue) / 100;
}

/**
 * Format currency with configurable decimal places
 * @deprecated Use formatCurrency with minimumFractionDigits/maximumFractionDigits options instead
 */
export function formatCurrencyWithDecimals(value: number, decimals: number = 0): string {
  return formatCurrency(value, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}
